import { Button, Fields } from '@ets-global/b2c-website-ui';
import { Field as BaseFastField, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { COUNTRIES } from '../../../graphql/queries';
import {
  NB_EMPLOYEE_OPTIONS,
  NB_STUDENT_OPTIONS,
  SEGMENTS_OPTIONS,
  YEARS_ACTIVITY_OPTIONS,
} from '../../../graphql/queries/optionsSalesForce';
import { useDataLayer, useRouter } from '../../../hooks';
import useQuery from '../../../hooks/useQuery';
import useYup from '../../../hooks/useYup';

const Step2 = ({ initialValues = {}, onSubmit = () => {}, onCancel = () => {} }) => {
  const {
    currentRoute: { params },
  } = useRouter();
  const { pushEventLoadedB2BContactFunnel } = useDataLayer();
  const { t } = useTranslation();
  const { Yup } = useYup();

  useEffect(() => {
    pushEventLoadedB2BContactFunnel({
      stepName: 'company',
      stepNumber: 2,
    });
  }, []);

  const [subSegments, setSubSegments] = useState([]);
  const [subSegmentIsInitialized, setSubSegmentIsInitialized] = useState(false);

  const onSegmentChange = (segmentId) => {
    const subSegments = segments.find((segment) => segment.value === segmentId).subSegments;
    setSubSegments(subSegments);
  };

  const B2BContactFormStep2Schema = Yup.object().shape({
    organisationLegalName: Yup.string().required(),
    organisationMainAddressFirst: Yup.string().required(),
    organisationMainAddressSecond: Yup.string(),
    organisationCity: Yup.string().required(),
    organisationZipCode: Yup.string().required(),
    organisationCountry: Yup.string().required(),
    organisationPhone: Yup.string().phoneNumber().required(),
    organisationWebSite: Yup.string(),
    segment: Yup.string().required(),
    subSegment: Yup.string().required(),
    organisationYearsActivity: Yup.string(),
    organisationNbEmployees: Yup.string(),
    organisationNbStudents: Yup.string(),
  });

  const { data: countriesData, loading: countriesLoading } = useQuery(COUNTRIES, {
    variables: { lang: params.locale },
  });
  let countries = [];
  if (!countriesLoading) {
    countries = countriesData.countries
      .map(({ emoIso2: value, name: label }) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
    countries.unshift({ value: '', label: '' });
  }

  const { data: segmentsData, loading: segmentsLoading } = useQuery(SEGMENTS_OPTIONS, {});
  let segments = [];
  if (!segmentsLoading) {
    segments = segmentsData.salesforce.segmentsOptions.map(({ option: { value }, subSegmentsOptions }) => {
      const subSegmentValues = subSegmentsOptions.map(({ value: SubSegmentValue }) => ({
        label: t(`contact.form.options.subSegment.${SubSegmentValue}`),
        value: SubSegmentValue,
      }));
      subSegmentValues.unshift({ label: '', value: '' });

      if (
        initialValues.segment &&
        value === initialValues.segment &&
        JSON.stringify(subSegments) !== JSON.stringify(subSegmentValues) &&
        !subSegmentIsInitialized
      ) {
        setSubSegmentIsInitialized(true);
        setSubSegments(subSegmentValues);
      }

      return { label: t(`contact.form.options.segment.${value}`), value, subSegments: subSegmentValues };
    });
    segments.unshift({ label: '', value: '' });
  }

  const { data: yearsActivityData, loading: yearsActivityLoading } = useQuery(YEARS_ACTIVITY_OPTIONS, {});
  let yearsActivity = [];
  if (!yearsActivityLoading) {
    yearsActivity = yearsActivityData.salesforce.yearsActivityOptions.map(({ label, value }) => ({ label, value }));
    yearsActivity.unshift({ label: '', value: '' });
  }

  const { data: nbEmployeeData, loading: nbEmployeeLoading } = useQuery(NB_EMPLOYEE_OPTIONS, {});
  let nbEmployee = [];
  if (!nbEmployeeLoading) {
    nbEmployee = nbEmployeeData.salesforce.nbEmployeeOptions.map(({ label, value }) => ({ label, value }));
    nbEmployee.unshift({ label: '', value: '' });
  }

  const { data: nbStudentData, loading: nbStudentLoading } = useQuery(NB_STUDENT_OPTIONS, {});
  let nbStudent = [];
  if (!nbStudentLoading) {
    nbStudent = nbStudentData.salesforce.nbStudentOptions.map(({ label, value }) => ({ label, value }));
    nbStudent.unshift({ label: '', value: '' });
  }

  return (
    <Formik
      initialValues={{
        organisationLegalName: '',
        organisationMainAddressFirst: '',
        organisationMainAddressSecond: '',
        organisationCity: '',
        organisationZipCode: '',
        organisationCountry: '',
        organisationPhone: '',
        organisationWebSite: '',
        segment: '',
        subSegment: '',
        organisationYearsActivity: '',
        organisationNbEmployees: '',
        organisationNbStudents: '',
        ...initialValues,
      }}
      validationSchema={B2BContactFormStep2Schema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form className="contact-form">
          <div className="contact-form__main-inner">
            <div className="contact-form__main-column">
              <div className="contact-form__fieldset">
                <h3 className="contact-form__fieldset-legend">
                  <Trans>contact.form.legal-information</Trans>
                </h3>
                <Fields.FormikFastField
                  id="organisationLegalName"
                  name="organisationLegalName"
                  component={Fields.TextField}
                  label={t('contact.form.label.organisation.legal-name')}
                  autoComplete={'contact organization'}
                  required={true}
                />
                <Fields.FormikFastField
                  id="organisationMainAddressFirst"
                  name="organisationMainAddressFirst"
                  component={Fields.TextField}
                  label={t('contact.form.label.organisation.main-address.first')}
                  autoComplete={'contact address-line1'}
                  required={true}
                />
                <Fields.FormikFastField
                  id="organisationMainAddressSecond"
                  name="organisationMainAddressSecond"
                  component={Fields.TextField}
                  label={t('contact.form.label.organisation.main-address.second')}
                  autoComplete={'contact address-line2'}
                />
                <div className="contact-form__field-row">
                  <Fields.FormikFastField
                    id="organisationCity"
                    name="organisationCity"
                    component={Fields.TextField}
                    label={t('contact.form.label.organisation.main-address.city')}
                    autoComplete={'contact address-level2'}
                    required={true}
                  />
                  <Fields.FormikFastField
                    id="organisationZipCode"
                    name="organisationZipCode"
                    component={Fields.TextField}
                    label={t('contact.form.label.organisation.main-address.postal-code')}
                    autoComplete={'contact postal-code'}
                    required={true}
                  />
                </div>
                <Fields.FormikFastField
                  id="organisationCountry"
                  name="organisationCountry"
                  component={Fields.SelectField}
                  label={t('contact.form.label.organisation.main-address.country')}
                  autoComplete={'contact country'}
                  options={countries}
                  required={true}
                />
              </div>
            </div>
            <div className="contact-form__main-column">
              <div className="contact-form__fieldset">
                <h3 className="contact-form__fieldset-legend">{t('contact.form.organisation-contact-information')}</h3>
                <BaseFastField
                  component={Fields.PhoneNumberField}
                  id="organisationPhone"
                  name="organisationPhone"
                  className={'field--dotted'}
                  labels={[t('contact.form.label.country'), t('contact.form.label.organisation.phone')]}
                  required={true}
                />
                <Fields.FormikFastField
                  id="organisationWebSite"
                  name="organisationWebSite"
                  component={Fields.TextField}
                  label={t('contact.form.label.organisation.website')}
                  autoComplete={'contact url'}
                />
              </div>

              <div className="contact-form__fieldset">
                <h3 className="contact-form__fieldset-legend">
                  <Trans>contact.form.organisation-additional-information</Trans>
                </h3>
                <Fields.FormikFastField
                  id="segment"
                  name="segment"
                  component={Fields.SelectField}
                  label={t('contact.form.label.organisation.segment')}
                  autoComplete={'contact segment'}
                  options={segments}
                  onChange={(e) => {
                    setFieldValue('subSegment', '');
                    onSegmentChange(e.target.value);
                  }}
                  required={true}
                />
                <Fields.FormikFastField
                  id="subSegment"
                  name="subSegment"
                  component={Fields.SelectField}
                  label={t('contact.form.label.organisation.subSegment')}
                  autoComplete={'contact subSegment'}
                  options={subSegments}
                  required={true}
                />
                <Fields.FormikFastField
                  id="organisationYearsActivity"
                  name="organisationYearsActivity"
                  component={Fields.SelectField}
                  label={t('contact.form.label.organisation.years-of-activity')}
                  autoComplete={'contact organisationYearsActivity'}
                  options={yearsActivity}
                />
                <div className="contact-form__field-row">
                  <Fields.FormikFastField
                    id="organisationNbEmployees"
                    name="organisationNbEmployees"
                    component={Fields.SelectField}
                    label={t('contact.form.label.organisation.employees')}
                    autoComplete={'contact organisationNbEmployees'}
                    options={nbEmployee}
                  />
                  <Fields.FormikFastField
                    id="organisationNbStudents"
                    name="organisationNbStudents"
                    component={Fields.SelectField}
                    label={t('contact.form.label.organisation.students')}
                    autoComplete={'contact organisationNbStudents'}
                    options={nbStudent}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="contact-form__buttons">
            <Button type="submit" disabled={isSubmitting} aria-label={t('contact.form.button.next')}>
              <Trans>contact.form.button.next</Trans>
            </Button>
            <Button
              type="button"
              className="button--white"
              onClick={onCancel}
              aria-label={t('contact.form.button.back')}
            >
              <Trans>contact.form.button.back</Trans>
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

Step2.propTypes = {
  initialValues: PropTypes.shape({
    organisationLegalName: PropTypes.string,
    organisationMainAddressFirst: PropTypes.string,
    organisationMainAddressSecond: PropTypes.string,
    organisationCity: PropTypes.string,
    organisationZipCode: PropTypes.string,
    organisationCountry: PropTypes.string,
    organisationPhone: PropTypes.string,
    organisationWebSite: PropTypes.string,
    segment: PropTypes.string,
    subSegment: PropTypes.string,
    organisationYearsActivity: PropTypes.string,
    organisationNbEmployees: PropTypes.string,
    organisationNbStudents: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default Step2;
